import * as React from 'react';
import {
  CheckCircle,
  Cube,
  Gear,
  GitPullRequest,
  House,
  MagnifyingGlass,
  Robot,
  ShieldChevron,
  ShieldWarning,
} from '@phosphor-icons/react';
import { useMutation, useQuery } from 'convex/react';
import { useUser } from '@clerk/tanstack-react-start';
import { useNavigate } from '@tanstack/react-router';
import { api } from '@/convex';
import { useOrgContext } from '@/hooks/useOrgContext';
import { useProjectContext } from '@/hooks/useProjectContext';
import {
  buildFindingsPath,
  buildGovernancePath,
  buildMcpPath,
  buildProjectSearch,
  buildPullRequestsPath,
  buildDashboardPath,
  buildSettingsPath,
} from '@/lib/routes';
import s from './DashboardLayout.module.scss';

// ── Search Dialog ────────────────────────────────────────────────

interface SearchItem {
  id: string;
  label: string;
  group: 'page' | 'component';
  icon: typeof House;
  meta?: string;
  href: string;
  search?: Record<string, string>;
}

export function SearchDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const { org } = useOrgContext();
  const orgSlug = org?.slug ?? null;
  const { selectedProjectId, resolvedSlug } = useProjectContext();
  const [query, setQuery] = React.useState('');
  const [activeIndex, setActiveIndex] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const searchObj = React.useMemo(
    () => buildProjectSearch(resolvedSlug),
    [resolvedSlug],
  );

  const components = useQuery(
    api.componentRegistry.listByProject,
    selectedProjectId ? { projectId: selectedProjectId } : 'skip',
  );

  React.useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const pages: SearchItem[] = React.useMemo(() => {
    if (!orgSlug) return [];
    return [
      {
        id: 'p-dashboard',
        label: 'Dashboard',
        group: 'page',
        icon: House,
        href: buildDashboardPath(orgSlug),
      },
      {
        id: 'p-pull-requests',
        label: 'Pull Requests',
        group: 'page',
        icon: GitPullRequest,
        href: buildPullRequestsPath(orgSlug),
      },
      {
        id: 'p-findings',
        label: 'Backlog',
        group: 'page',
        icon: ShieldWarning,
        href: buildFindingsPath(orgSlug),
      },
      {
        id: 'p-governance',
        label: 'Governance',
        group: 'page',
        icon: ShieldChevron,
        href: buildGovernancePath(orgSlug),
      },
      {
        id: 'p-mcp',
        label: 'Developer Setup',
        group: 'page',
        icon: Robot,
        href: buildMcpPath(orgSlug),
      },
      {
        id: 'p-settings',
        label: 'Settings',
        group: 'page',
        icon: Gear,
        href: buildSettingsPath(orgSlug),
      },
    ];
  }, [orgSlug]);

  const componentItems: SearchItem[] = React.useMemo(() => {
    if (!components || !orgSlug) return [];
    return components
      .filter((c) => !(c as any).isSubcomponent)
      .map((c) => {
        const componentKey = (c as any).componentKey as string | undefined;
        return {
          id: `c-${c._id}`,
          label: c.displayName,
          group: 'component' as const,
          icon: Cube,
          meta: (c as any).tier === 'core' ? 'Primitive' : 'Custom',
          // The standalone Components page is gone (brief 03 nav-cut); jump to
          // the Backlog filtered to this component's findings instead.
          href: buildFindingsPath(orgSlug),
          search: componentKey
            ? { ...(searchObj ?? {}), component: componentKey }
            : searchObj,
        };
      });
  }, [components, orgSlug, searchObj]);

  const filtered = React.useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return [...pages, ...componentItems.slice(0, 8)];
    return [...pages, ...componentItems].filter((item) =>
      item.label.toLowerCase().includes(q),
    );
  }, [query, pages, componentItems]);

  React.useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  function handleSelect(item: SearchItem) {
    onClose();
    navigate({
      to: item.href as any,
      search: item.search ?? searchObj,
    } as Parameters<typeof navigate>[0]);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && filtered[activeIndex]) {
      e.preventDefault();
      handleSelect(filtered[activeIndex]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  }

  const pageResults = filtered.filter((i) => i.group === 'page');
  const componentResults = filtered.filter((i) => i.group === 'component');

  return (
    <div className={s.dialogOverlay} onClick={onClose}>
      <div
        className={s.dialogPanel}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Search"
      >
        <div className={s.searchDialogInput}>
          <MagnifyingGlass size={16} className={s.searchDialogIcon} />
          <input
            ref={inputRef}
            className={s.searchDialogField}
            type="text"
            placeholder="Search pages, components..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <kbd className={s.searchDialogKbd}>ESC</kbd>
        </div>
        <div className={s.searchDialogBody}>
          {filtered.length === 0 ? (
            <div className={s.searchEmpty}>
              <MagnifyingGlass size={24} />
              <span>No results for &ldquo;{query}&rdquo;</span>
            </div>
          ) : (
            <>
              {pageResults.length > 0 && (
                <>
                  <div className={s.searchGroupLabel}>Pages</div>
                  {pageResults.map((item) => {
                    const globalIdx = filtered.indexOf(item);
                    return (
                      <button
                        key={item.id}
                        type="button"
                        className={s.searchResultItem}
                        data-active={
                          globalIdx === activeIndex ? true : undefined
                        }
                        onClick={() => handleSelect(item)}
                        onMouseEnter={() => setActiveIndex(globalIdx)}
                      >
                        <item.icon size={14} className={s.searchResultIcon} />
                        <span className={s.searchResultLabel}>
                          {item.label}
                        </span>
                      </button>
                    );
                  })}
                </>
              )}
              {componentResults.length > 0 && (
                <>
                  <div className={s.searchGroupLabel}>Components</div>
                  {componentResults.map((item) => {
                    const globalIdx = filtered.indexOf(item);
                    return (
                      <button
                        key={item.id}
                        type="button"
                        className={s.searchResultItem}
                        data-active={
                          globalIdx === activeIndex ? true : undefined
                        }
                        onClick={() => handleSelect(item)}
                        onMouseEnter={() => setActiveIndex(globalIdx)}
                      >
                        <item.icon size={14} className={s.searchResultIcon} />
                        <span className={s.searchResultLabel}>
                          {item.label}
                        </span>
                        {item.meta && (
                          <span className={s.searchResultMeta}>
                            {item.meta}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </>
              )}
            </>
          )}
        </div>
        <div className={s.searchDialogFooter}>
          <span className={s.searchFooterHint}>
            <kbd className={s.searchFooterKbd}>&uarr;</kbd>
            <kbd className={s.searchFooterKbd}>&darr;</kbd>
            navigate
          </span>
          <span className={s.searchFooterHint}>
            <kbd className={s.searchFooterKbd}>&crarr;</kbd>
            select
          </span>
          <span className={s.searchFooterHint}>
            <kbd className={s.searchFooterKbd}>esc</kbd>
            close
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Invite Dialog ────────────────────────────────────────────────

export function InviteDialog({ onClose }: { onClose: () => void }) {
  const { orgId, org } = useOrgContext();
  const { user } = useUser();
  const createInvite = useMutation(api.invites.create);
  const [email, setEmail] = React.useState('');
  const [role, setRole] = React.useState<'admin' | 'member'>('member');
  const [error, setError] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    inputRef.current?.focus();
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !orgId || !user?.id) return;

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }

    setSending(true);
    setError('');
    try {
      await createInvite({
        orgId,
        email: trimmed,
        role,
        invitedBy: user.id,
        invitedByName: user.fullName ?? user.primaryEmailAddress?.emailAddress,
      });
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invite');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className={s.dialogOverlay} onClick={onClose}>
      <div
        className={s.dialogPanel}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Invite team member"
        style={{ width: 440 }}
      >
        {sent ? (
          <div className={s.inviteDialogInner}>
            <div className={s.inviteSuccess}>
              <CheckCircle
                size={36}
                weight="fill"
                className={s.inviteSuccessIcon}
              />
              <div className={s.inviteSuccessTitle}>Invite sent</div>
              <div className={s.inviteSuccessDesc}>
                An invitation has been sent to <strong>{email.trim()}</strong>
              </div>
            </div>
            <div className={s.inviteDialogActions}>
              <button
                type="button"
                className={s.inviteSubmitBtn}
                onClick={onClose}
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <form className={s.inviteDialogInner} onSubmit={handleSubmit}>
            <h2 className={s.inviteDialogTitle}>Invite team member</h2>
            <p className={s.inviteDialogDesc}>
              Send an invitation to join {org?.name ?? 'this organization'}.
            </p>
            <div className={s.inviteForm}>
              <div>
                <label className={s.inviteFieldLabel} htmlFor="invite-email">
                  Email address
                </label>
                <input
                  ref={inputRef}
                  id="invite-email"
                  className={s.inviteFieldInput}
                  type="email"
                  placeholder="colleague@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={sending}
                  autoComplete="off"
                />
              </div>
              <div>
                <label className={s.inviteFieldLabel} htmlFor="invite-role">
                  Role
                </label>
                <select
                  id="invite-role"
                  className={s.inviteRoleSelect}
                  value={role}
                  onChange={(e) =>
                    setRole(e.target.value as 'admin' | 'member')
                  }
                  disabled={sending}
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              {error && <p className={s.inviteError}>{error}</p>}
              <div className={s.inviteDialogActions}>
                <button
                  type="button"
                  className={s.inviteCancelBtn}
                  onClick={onClose}
                  disabled={sending}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={s.inviteSubmitBtn}
                  disabled={!email.trim() || sending}
                >
                  {sending ? 'Sending...' : 'Send invite'}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
